/* eslint-disable react/prop-types */
/*
Author: Gilang Romadhanu Tartila
*/

import React from 'react';
import { useDispatch } from 'react-redux';
import {
  createStackNavigator,
  CardStyleInterpolators,
} from '@react-navigation/stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { isWeb, isNative } from '@global/screens';

import {
  setTitleHeader,
  setDefaultStackOption,
  hasNoAccess,
} from '@global/helpers';

import HamburgerButton from '@components/navbar/HamburgerButton';
import NotificationSettingButton from '@components/navbar/NotificationSettingButton';
import BackButton from '@components/navbar/BackButton';
import MoreButton from '@components/navbar/MoreButton';

import SalesOrderScreen from '@/screens/transaction/sales-order/SalesOrderScreen';
import SalesOrderDetailScreen from '@/screens/transaction/sales-order/SalesOrderDetailScreen';
import SalesOrderInvoiceScreen from '@/screens/transaction/sales-order/SalesOrderInvoiceScreen';
import SalesOrderDeliveryScreen from '@/screens/transaction/sales-order/SalesOrderDeliveryScreen';
import SalesOrderDocumentScreen from '@/screens/transaction/sales-order/SalesOrderDocumentScreen';
import SalesOrderFormScreen from '@/screens/transaction/sales-order/SalesOrderFormScreen';
import SalesOrderPaymentProofScreen from '@/screens/transaction/sales-order/SalesOrderPaymentProofScreen';

import { showPopup } from '@/store/modal';

const Stack = createStackNavigator();

const SalesOrderNavigator = ({ navigation }) => {
  const insets = useSafeAreaInsets();
  const dispatch = useDispatch();

  return (
    <Stack.Navigator
      initialRouteName="SalesOrderScreen"
      screenOptions={(props) => ({
        headerLeft: () => <HamburgerButton {...props} />,
        headerRight: () => <NotificationSettingButton {...props} />,
        ...setDefaultStackOption(insets, CardStyleInterpolators, true),
      })}>
      <Stack.Screen
        name="SalesOrderScreen"
        component={SalesOrderScreen}
        options={(props) => {
          let isBack = false;
          let title = 'Sales Order';
          if (props?.route?.params?.titleScreen) {
            isBack = true;
            title = `${title} ${
              !props?.route?.params?.isHome
                ? props.route.params.titleScreen
                : ''
            }`;
          }
          const showFilter = () => {
            dispatch(showPopup({ name: 'showFilter', value: 'sales-order' }));
          };
          const showExport = () => {
            dispatch(
              showPopup({ name: 'showOrdersExportOptions', value: true }),
            );
          };
          const showImport = () => {
            dispatch(showPopup({ name: 'showImportModalSO', value: true }));
          };
          const exportSo = !hasNoAccess([
            'superadmin',
            'general',
            'sales_admin',
            'salesman',
            'sales_supervisor',
            'chief_of_staff',
          ]);
          const menus = [
            {
              type: 'function',
              name: 'Export',
              callback: showExport,
            },
            { type: 'function', name: 'Filter', callback: showFilter },
          ];
          if (exportSo) {
            menus.push({
              type: 'function',
              name: 'Import',
              callback: showImport,
            });
          }
          const goBack = () => {
            if (props?.route?.params?.isHome) {
              props.navigation.replace('SalesOrderScreen', {
                customerId: '',
                titleScreen: '',
              });
              props.navigation.navigate('HomeNavigator');
            } else if (
              props?.route?.params?.customerId &&
              props?.route?.params?.titleScreen
            ) {
              const customerId = JSON.parse(
                JSON.stringify(props.route.params.customerId),
              );
              const titleScreen = JSON.parse(
                JSON.stringify(props.route.params.titleScreen),
              );
              props.navigation.replace('SalesOrderScreen', {
                customerId: '',
                titleScreen: '',
              });
              props.navigation.navigate('CustomerNavigator', {
                screen: 'CustomerDetailScreen',
                params: {
                  id: customerId,
                  name: titleScreen,
                },
              });
            }
          };
          return {
            title: setTitleHeader(title),
            headerLeft: () =>
              isBack ? (
                <BackButton {...props} onPress={() => goBack()} />
              ) : (
                <HamburgerButton {...props} />
              ),
            headerRight: () =>
              isWeb ? (
                <NotificationSettingButton {...props} />
              ) : (
                <MoreButton menu={menus} {...props} />
              ),
          };
        }}
      />
      <Stack.Screen
        name="SalesOrderDetailScreen"
        component={SalesOrderDetailScreen}
        options={(props) => {
          const route = props.route;
          let title = 'Sales Order Detail';
          if (route?.params?.titleScreen) {
            title = route.params.titleScreen;
          }
          const goBackKey = props?.route?.params?.go_back_key || '';

          return {
            title: setTitleHeader(title),
            headerLeft: () => (
              <BackButton
                {...props}
                // isJump forces the BackButton's customBack to NAVIGATE (pop to the
                // existing screen) instead of navigation.replace(), which would stack
                // a new SalesOrderScreen and remount the list (losing scroll + pages).
                isJump={true}
                notUseLastRoute={true}
                onPress={
                  props?.route?.params?.isFilter && isNative
                    ? () =>
                        props.navigation.navigate('SalesOrderScreen', {
                          key: goBackKey,
                          isFilter: props?.route?.params?.isFilter,
                        })
                    : goBackKey
                    ? () => props.navigation.goBack(goBackKey)
                    : false
                }
                customBack={
                  props?.route?.params?.activityId
                    ? {
                        screen: 'SalesActivityNavigator',
                        params: {
                          screen: 'SalesActivityScreen',
                          params: {
                            id: props.route.params.activityId,
                            direct: 'orders',
                          },
                        },
                      }
                    : props?.route?.params?.isTask
                    ? {
                        screen: 'TaskNotificationNavigator',
                        params: {
                          screen: 'TaskNotificationScreen',
                          params: {
                            task: true,
                          },
                        },
                      }
                    : { screen: 'SalesOrderScreen' }
                }
              />
            ),
            headerRight: () =>
              isWeb ? <NotificationSettingButton {...props} /> : false,
          };
        }}
      />
      <Stack.Screen
        name="SalesOrderInvoiceScreen"
        component={SalesOrderInvoiceScreen}
        options={(props) => ({
          title: setTitleHeader('Invoice'),
          headerLeft: () => {
            const isCustomer = props?.route?.params?.customerId ? true : false;
            let params = {
              screen: 'SalesOrderDetailScreen',
              params: {
                id: props.route.params?.salesOrderId || '',
                name: props.route.params?.salesOrderName || '',
                titleScreen: props.route.params?.salesOrderName || '',
                ...(props?.route?.params?.isTask ? { isTask: true } : {}),
              },
            };
            if (isCustomer) {
              params = {
                screen: 'CustomerNavigator',
                params: {
                  screen: 'CustomerDetailScreen',
                  params: {
                    id: props.route.params?.customerId || '',
                    name: props.route.params?.titleScreen || '',
                  },
                },
              };
            }
            return (
              <BackButton {...props} isJump={isCustomer} customBack={params} />
            );
          },
          headerRight: () =>
            isWeb ? <NotificationSettingButton {...props} /> : false,
        })}
      />
      <Stack.Screen
        name="SalesOrderDeliveryScreen"
        component={SalesOrderDeliveryScreen}
        options={(props) => ({
          title: setTitleHeader('Delivery'),
          headerLeft: () => (
            <BackButton
              {...props}
              customBack={{
                screen: 'SalesOrderDetailScreen',
                params: {
                  id: props.route.params.salesOrderId,
                  name: props.route.params.salesOrderName,
                  titleScreen: props.route.params.salesOrderName,
                  ...(props?.route?.params?.isTask ? { isTask: true } : {}),
                },
              }}
            />
          ),
          headerRight: () =>
            isWeb ? <NotificationSettingButton {...props} /> : false,
        })}
      />
      <Stack.Screen
        name="SalesOrderPaymentProofScreen"
        component={SalesOrderPaymentProofScreen}
        options={(props) => ({
          title: setTitleHeader('Payment Proof'),
          headerLeft: () => (
            <BackButton
              {...props}
              customBack={{
                screen: 'SalesOrderDetailScreen',
                params: {
                  id: props.route.params.salesOrderId,
                  name: props.route.params.salesOrderName,
                  titleScreen: props.route.params.salesOrderName,
                  ...(props?.route?.params?.isTask ? { isTask: true } : {}),
                },
              }}
            />
          ),
          headerRight: () =>
            isWeb ? <NotificationSettingButton {...props} /> : false,
        })}
      />
      <Stack.Screen
        name="SalesOrderDocumentScreen"
        component={SalesOrderDocumentScreen}
        options={(props) => ({
          title: setTitleHeader('Attachment'),
          headerLeft: () => (
            <BackButton
              {...props}
              customBack={{
                screen: 'SalesOrderDetailScreen',
                params: {
                  id: props.route.params.salesOrderId,
                  name: props.route.params.salesOrderName,
                  titleScreen: props.route.params.salesOrderName,
                  ...(props?.route?.params?.isTask ? { isTask: true } : {}),
                },
              }}
            />
          ),
          headerRight: () =>
            isWeb ? <NotificationSettingButton {...props} /> : false,
        })}
      />
      <Stack.Screen
        name="SalesOrderFormScreen"
        component={SalesOrderFormScreen}
        options={(props) => {
          const route = props.route;
          let title = 'Add Sales Order';
          if (route.params && route.params.order_type == 'spreading') {
            title = 'Add Sales Order Spreading';
          }

          // let backParam = { screen: 'SalesOrderScreen' }
          if (route.params && route.params.titleScreen) {
            title = 'Edit Sales Order';
            if (route.params.order_type == 'spreading') {
              title = 'Edit Sales Order Spreading';
            }
            // backParam = { screen: 'SalesOrderDetailScreen', params: route.params }
          }
          return {
            title: setTitleHeader(title),
            // headerLeft: () => <BackButton {...props} customBack={backParam} />,
            headerRight: isWeb
              ? () => <NotificationSettingButton {...props} />
              : false,
          };
        }}
      />
    </Stack.Navigator>
  );
};

export default SalesOrderNavigator;
